/**
 * Module 6: Your Body Already Knows
 *
 * In which Theron hides the cushions, Daria rocks like a skiff, and nobody
 * sits.
 * Primary character: Theron | Supporting: Philemon, Xenia, Daria
 *
 * REWORKED 2026-10-04 to the six rules of the "less laboured" pass
 * (docs/CONTENT_REVIEW_2026-10-04.md), after Evening 1:
 *
 *   1. One idea, shown once. Tonight's is: settle the body first (feet,
 *      shoulders, jaw, three breaths), then stand still and speak. Daria does
 *      it once, about her breakfast. Nobody says what it means.
 *   2. Something happens tonight. Theron has cleared the courtyard and nobody
 *      is allowed to sit, the reader included.
 *   3. Somebody is wrong and stays wrong. Daria: "If I stand still, they'll
 *      see me shake." Philemon tells her some will. She would still rather
 *      rock when the evening ends. (Theron is wrong too, briefly: "stand as
 *      if you own the ship and your blood will believe you". The one box in
 *      the open answers him.)
 *   4. The reader's turn: Theron makes the reader stand and asks where it
 *      tightened. Each reply is the instruction for that part of the body.
 *   5. One box in the open, where Theron makes his claim about posture: the
 *      honest account of the power pose. Everything else is in the notes.
 *   6. One exercise in the scene (the ritual, once), one to take away
 *      (standing still while you talk), and the Presence Prep tool.
 *
 * The ritual is run ONCE in the story, on Daria. Do not add a second run.
 *
 * CONTINUITY this evening must keep:
 *   - One week to the gathering. Two weeks since the reader last saw Daria
 *     (Evening 4, three weeks out).
 *   - The rites (Evening 5 ends before them; this is the first the reader
 *     hears): Philemon's voice broke on the third sentence, twice more before
 *     the end, and he stayed. The widow held both his hands and called it the
 *     truest thing anyone had said of Eudoros all day. Theron was there
 *     (Evening 5: "We will be there tomorrow. All of us."); the reader was
 *     not (Evening 5's preview: "Come back after the rites... he will want to
 *     find the lamps lit", which is why the lamps are lit in the first line).
 *   - "Two eulogies, twenty years apart" answers his mother's line in
 *     Evening 5 ("it didn't sound like you knew him").
 *   - Evening 5's preview has Theron say "Daria is mine next. Come ready to
 *     stand." Nobody sits tonight.
 *   - Daria rocks "like a skiff at anchor" (Evening 7: "She is rocking on her
 *     feet again"). Her wax tablet comes down from her chest tonight
 *     (Evening 7: "The wax tablet is back against her chest").
 *   - She does not give her speech here. Evening 7 is where its seventeen
 *     points arrive; the preview only has her adding to it.
 *   - Evening 7's preview has Theron say "Bring your three words. And your feet."
 *
 * FACTS: no study was added. The posture account keeps every hedge it had on
 * 4 October (register rows 54 to 57), and the first-impressions study moved
 * from a `paragraph` into the notes with its "faces, not feet" caveat. The
 * only breathing claim is the app's canonical line, "linked with a calmer
 * body", in the notes. See
 * docs/audits/finding-your-voice-2026-10/register-modules-4-6.md.
 */

export const MODULE_06_CONTENT = [
  // --- After the rites ---
  {
    type: 'narration',
    text: 'The lamps are lit and the cushions are gone. The low table stands on its end against the wall, and where it used to be there is only bare stone. It is the first evening the gate has been open since the rites, and you had come braced for grief. What you find is Theron, pacing out the empty courtyard like a man measuring a ship\'s hold.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'He has put my cushions in the storeroom. Nobody sits tonight, he says. That includes you.',
  },
  {
    type: 'narration',
    text: 'Philemon is on his feet too, and he comes over before you have worked out how to ask.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You were not there, so have it from me before Theron improves it. My voice broke on the third sentence. I stopped. I breathed. I went on. It broke twice more before the end.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'And the room was utterly still. Afterwards his widow held both my hands and told me it was the truest thing anyone had said of him all day.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Two eulogies, twenty years apart. Your mother would have known him from this one.',
  },
  {
    type: 'narration',
    text: 'Philemon nods at the ground. Nobody hurries him.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I heard every word of it. I also watched his feet, because I always do. The voice went three times. The feet, not once.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Fifteen years he has been telling me where my feet are.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'And now I am telling her.',
  },

  // --- The skiff ---
  {
    type: 'heading',
    text: 'The skiff',
  },
  {
    type: 'narration',
    text: 'He means Daria, by the archway, with her wax tablet held to her chest again, the way it was when she first came through the gate two weeks ago. The gathering is one week away.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'He made me say my speech on the harbour steps this morning. A fish-seller applauded.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'He applauded when you stopped. She rocks. Left foot, right foot, like a skiff at anchor. I was seasick by the second sentence. Out in the middle, Daria. Not the speech. I have heard the speech. Tell us what you ate this morning.',
  },
  {
    type: 'narration',
    text: 'Daria walks out onto the bare stone. There is nothing out there to stand behind.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Bread. An onion. Half a fig.',
  },
  {
    type: 'narration',
    text: 'It has started already, the tablet riding at her chest like a lamp on a mast.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Feet.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'I know they\'re moving. If I stand still, they\'ll see me shake.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'They see you rock. From the back wall, with the sun in their eyes.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Rocking only looks like rocking. Shaking looks like fear.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I rocked once, in Syracuse, when I was young. The buyer told me afterwards: "You looked like a man who wanted to run away. Why would I trust you with my money?" He kept it.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'So stand like someone who is not afraid. Fists on your hips. Chest out. Stand as if you own the ship, and your blood will believe you.',
  },
  {
    type: 'narration',
    text: 'Daria tries it: fists, chest, chin.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'I feel like a child in my father\'s breastplate.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I know that stance. I borrowed one like it for years.',
  },
  {
    type: 'research',
    title: 'Theron is half right',
    source: 'Carney, Cuddy and Yap, Psychological Science, 2010; Körner and colleagues, Psychological Bulletin, 2022',
    paragraphs: [
      'In 2010 Dana Carney, Amy Cuddy and Andy Yap reported that expansive "power poses" raised testosterone and lowered cortisol. A larger study in 2015 found no effect on hormones. In 2016 Carney wrote that she no longer believed the effect was real.',
      'A 2022 review of 128 studies found something smaller: people placed in upright or open postures reported feeling somewhat more confident than people in slumped ones. Most had no neutral posture for comparison, so nobody can yet say whether standing tall lifts you or slumping drags you down.',
    ],
  },

  // --- On the gangplank: the ritual, once ---
  {
    type: 'heading',
    text: 'On the gangplank',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Fine. Put the captain down. This is what I do on the gangplank before any room with money in it, and it takes less time than pouring a round of wine. Feet under your hips. Both of them. Let the stone hold you up. It is better at it than you are.',
  },
  {
    type: 'narration',
    text: 'She plants them. The rocking stops, and her shoulders rise to make up for it.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Shoulders. They are up by your ears. They have been carrying your fear for you. Let them put it down. Now the jaw. Teeth apart.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'My teeth are...',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Apart. Three breaths, slower than you want, all the way out each time. Then look at us and finish your breakfast.',
  },
  {
    type: 'narration',
    text: 'Three breaths. On the third, the tablet comes down from her chest and hangs at her side.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Bread. An onion. Half a fig. The other half had a wasp in it.',
  },
  {
    type: 'narration',
    text: 'She has not moved. The hem of her dress trembles at the knee, a little, and you see it and forget it, because you are wondering about the wasp.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'What became of the wasp?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'It\'s still in the fig. I left them to it.',
  },
  {
    type: 'narration',
    text: 'Theron laughs first. Daria does not. She is looking from face to face.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'You were all watching my knees.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Nobody was watching your knees.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I was. I look at everyone\'s. It is how I find the ones like me.',
  },
  {
    type: 'narration',
    text: 'Theron has no answer to that. He looks round for something else to fix, and finds you.',
  },
  {
    type: 'yourTurn',
    id: 'm6-where-it-tightened',
    character: 'theron',
    speaker: 'Theron',
    prompt: 'Up. Yes, you. Nobody sits tonight. Stand the way you would if this whole courtyard had just gone quiet to hear you. There. Something in you tightened. Where?',
    options: [
      {
        label: 'My feet. They want to move.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Another skiff. Feet as wide as your hips, weight on both, knees soft. Now press your big toes into the floor, hard, as if you were holding a coin down with each. A foot that is pressing cannot wander. Speak from there.',
        },
      },
      {
        label: 'My shoulders. They are up by my ears.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Then do not order them down. They will not obey you. Lift them instead, right up to your ears, and hold for three heartbeats. Now let go. Where they land is where they live. Do it twice before any room.',
        },
      },
      {
        label: 'My jaw. My teeth are clenched.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Teeth apart. Lips closed if you like, but teeth apart, and your tongue down off the roof of your mouth. No word gets out through a barred gate. Yawn if you need to. Philemon is used to it.',
        },
      },
      {
        label: 'My breath. It has gone high and quick.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'Mine left on the third sentence. Do what I did: breathe out, not in. All of it, slowly, as if cooling broth. The next breath arrives by itself, and you speak on that one. The room waited for me. It will wait for you.',
        },
      },
    ],
  },
  {
    type: 'exercise',
    number: 1,
    title: 'On your feet',
    instructions: 'Now do the whole of Theron\'s ritual once, in order, before you read on. Stand if you can. If you speak seated, do it seated, upright, with your weight even on the chair.',
    prompts: [
      'Feet: as wide as your hips, weight even, knees unlocked. Feel the floor take your weight.',
      'Shoulders: lift them to your ears, hold for a breath, and let them drop.',
      'Jaw: teeth apart, tongue resting low in your mouth.',
      'Three breaths, in through the nose and out through the mouth, each out-breath slower than the in-breath.',
      'Look up at whatever is in front of you and say one plain sentence aloud. What you ate this morning will do.',
    ],
  },

  // --- One week ---
  {
    type: 'heading',
    text: 'One week',
  },
  {
    type: 'narration',
    text: 'Daria has been turning something over while you stood.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'So they will see.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Some will.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Then I\'d still rather rock.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Harbour steps. Seven mornings, feet first. The fish-seller reports to me.',
  },
  {
    type: 'narration',
    text: 'Xenia fetches her cushions back from the storeroom herself, and puts one in your arms without asking, since you are on your feet anyway. Daria has not left the middle of the courtyard. She is telling the lamps, quietly, what she means to eat tomorrow, and her feet are where she put them. From the wall you cannot tell whether her knees are shaking. You notice that you looked.',
  },

  {
    type: 'divider',
  },

  // --- To take away ---
  {
    type: 'heading',
    text: 'Before the next evening',
  },
  {
    type: 'exercise',
    number: 2,
    title: 'The harbour steps',
    instructions: 'Daria\'s homework, and yours. Once a day until you next have to speak, run the ritual and then talk aloud for about two minutes about something that does not matter. Your day will do. The rule is the one she hates: your feet stay where you put them.',
    prompts: [
      'Let your hands stay quiet unless they have something to say.',
      'When you feel the pull to shift, sway or pace, notice it and let it pass without moving.',
      'Record one run and watch it back once. Look for the thing Daria is afraid of. How much of the shaking can you see?',
      'Then take it somewhere real. Before one conversation, call or meeting this week, run the ritual out of sight, walk in and begin.',
    ],
  },
  {
    type: 'toolLink',
    toolId: 'presence-prep',
    title: 'Presence Prep',
    text: 'Theron\'s tool paces the whole ritual for you, a step at a time, with a circle to breathe along with, and ends on a look at the room. It takes about a minute and moves on by itself, so your hands stay free.',
  },

  // --- The research, for whoever wants it ---
  {
    type: 'notes',
    title: 'What we know now',
    intro: 'Theron can only tell you what Syracuse and the gangplank taught him. Here is what has been tested since, and which parts are only our own view.',
    items: [
      {
        title: 'How fast a stranger is judged',
        paragraphs: [
          'Theron\'s buyer was not unusual in judging so fast. In 2006 Janine Willis and Alexander Todorov at Princeton showed people unfamiliar faces for a tenth of a second and asked them to rate traits such as trustworthiness and competence. The snap ratings closely matched those made with no time limit.',
          'That study used faces, not feet, so it cannot tell us what a restless stance costs a speaker. It does show that people begin judging a stranger within a fraction of a second, well before the first word.',
        ],
        source: 'Willis and Todorov, Psychological Science, 2006',
      },
      {
        title: 'The rest of the posture story',
        paragraphs: [
          'The power pose was the best-known claim from a line of research called embodied cognition, which holds that thinking and feeling are shaped by the state of the body. In the 2010 study people held two expansive poses for a minute each. They also reported feeling more powerful, and closed, hunched poses were reported to do the opposite.',
          'The 2022 review, by Robert Körner and colleagues, found that people in upright or open postures also reported feeling more positive, with hardly any effect on hormones. Whichever way the effect runs, it is reason enough to stand upright and open before you speak, without expecting your hormones to change.',
        ],
        source: 'Carney, Cuddy and Yap, Psychological Science, 2010; Körner and colleagues, Psychological Bulletin, 2022',
      },
      {
        title: 'The ritual is ours',
        paragraphs: [
          'Feet, shoulders, jaw, three breaths is the course\'s own practice. We have not tested it as a set, and we would not promise that three breaths will calm you. The most we can say is that slow breathing with a long out-breath is linked with a calmer body.',
          'It has no pose in it on purpose. A prescribed stance or gesture is a costume, as Daria found, and we think it opens the same gap as a borrowed voice. The ritual only asks you to put down the tension that stops your body doing what it already knows how to do.',
        ],
      },
      {
        title: 'Standing still',
        paragraphs: [
          'This is our observation and not a finding. Anxious speakers tend to move too much: they shift their weight, sway, fidget and pace without purpose. To an audience that restlessness can read as discomfort, and it can cost the speaker some authority.',
          'Stillness here means a chosen, grounded way of standing, the physical equivalent of a pause. It does not mean going rigid. The catch is the one Daria names: with no movement to hide behind, it feels exposed.',
        ],
      },
    ],
  },

  // --- Module Summary ---
  {
    type: 'summary',
    characterId: 'theron',
    points: [
      'Before any room, settle the body in the same order: feet, shoulders, jaw, three breaths.',
      'People placed in upright or open postures report feeling somewhat more confident than people placed in slumped ones. The famous hormone claims did not hold up.',
      'Standing still feels exposed. Practise it first on something that does not matter, the way Daria did with her breakfast.',
    ],
    nextPreview: {
      dialogue: [
        {
          character: 'xenia',
          text: 'Seven days. The next time you come through that gate it will be the eve of the gathering.',
        },
        {
          character: 'daria',
          text: 'I thought of three more things my speech needs on the walk here. They all matter.',
        },
        {
          character: 'theron',
          text: 'Three more. Ships go down that way, one jar at a time.',
        },
      ],
    },
  },
];

export const MODULE_06_EXERCISES = [
  {
    number: 1,
    title: 'On your feet',
    description: 'Run Theron\'s ritual once: feet, shoulders, jaw, three breaths.',
    xpReward: 20,
  },
  {
    number: 2,
    title: 'The harbour steps',
    description: 'Once a day, run the ritual and then talk for two minutes without moving your feet.',
    xpReward: 20,
  },
];

export const MODULE_06_SUMMARY = {
  title: 'Your Body Already Knows',
  keyInsight: 'Settle the body first: feet, shoulders, jaw, three breaths.',
  characterQuote: {
    character: 'theron',
    text: 'Your shoulders have been carrying your fear for you. Let them put it down.',
  },
  takeaways: [
    'Before any room: feet, shoulders, jaw, three breaths.',
    'Standing upright and open makes people report feeling somewhat more confident; the hormone claims did not hold up.',
    'Standing still feels exposed. Practise it first on something that does not matter.',
  ],
};
